import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import {
  IconButton,
  Popover,
  Stack,
} from '@mui/material';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';

import AppointmentPreview from 'FRONTEND/components/Calendar/AppointmentPreview';
import { RootState } from 'FRONTEND/reducers/types';
import { selectExpertNextAppointment, selectExpertOngoingAppointment } from 'FRONTEND/selectors/appointmentSelectors';
import useCountDown from 'FRONTEND/utils/hooks/useCountDown';
import i18n from 'FRONTEND/utils/i18n';


type HighlightedAppointmentProps = {
  rtcServerUrl: string;
  canExtend?: boolean;
  hasMargin?: boolean;
  displayMode?: 'panel' | 'default';
}

const HighlightedAppointment = ({ rtcServerUrl, canExtend, hasMargin, displayMode }: HighlightedAppointmentProps) => {
  const locale = useSelector((state: RootState) => state.user.locale);

  // Refs for CSSTransition
  const appointmentNodeRef = useRef(null);
  const buttonNodeRef = useRef(null);

  useEffect(() => {
    dayjs.locale(locale);
  }, [locale]);

  // Modal management
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Appointment management
  const userId = useSelector((state: RootState) => state.user.id);
  const nextAppointment = useSelector((state: RootState) => selectExpertNextAppointment(state, userId, new Date().toJSON()));
  const ongoingAppointment = useSelector((state: RootState) => selectExpertOngoingAppointment(state, userId, new Date().toJSON()));

  const nextAppointmentWithin24h = nextAppointment && dayjs(nextAppointment.startDate).diff(dayjs(), 'hour') <= 24;

  // TODO: Not sure if still needed, was never fully implemented pre-redesign.
  // const handleOpenInCalendar = () => {
  //   dispatch(goToActorCalendar({ actorId: userId, actorType: EXPERT }));
  //   if (ongoingAppointment) {
  //     dispatch(pickEvent({ eventId: ongoingAppointment.id, actorId: userId }));
  //   } else if (nextAppointment) {
  //     dispatch(pickEvent({ eventId: nextAppointment.id, actorId: userId }));
  //   }
  // };

  // Rendering management
  const isExtended = useSelector((state: RootState) => state.sidePanel.isExtended);
  const id = isOpen ? 'simple-popover' : undefined;
  const countDown = useCountDown({ date: nextAppointment?.startDate });
  // The countdown belongs to `nextAppointment`, so only show it when that is the appointment
  // being rendered and it is close enough for the countdown to be meaningful.
  const nextAppointmentCountdown = nextAppointmentWithin24h ? countDown : undefined;

  return (
    <>
      <CSSTransition
        timeout={300}
        in={(!canExtend || isExtended) && Boolean(ongoingAppointment || nextAppointment)}
        unmountOnExit
        classNames='highlightedAppointment'
        nodeRef={appointmentNodeRef}
      >
        <div key='animationkey' className='highlightedAppointment' ref={appointmentNodeRef}>
          {(ongoingAppointment || nextAppointment) && (
            <>
              <Stack sx={{ m: hasMargin ? '2' : '' }}>
                <AppointmentPreview
                  appointment={ongoingAppointment || nextAppointment}
                  embedType='aside'
                  countdown={ongoingAppointment ? undefined : nextAppointmentCountdown}
                  isOngoingAppointment={Boolean(ongoingAppointment)}
                  displayMode={displayMode}
                  rtcServerUrl={rtcServerUrl}
                />
              </Stack>
            </>
          )}
        </div>
      </CSSTransition>

      <CSSTransition
        timeout={300}
        in={canExtend && !isExtended && Boolean(nextAppointment)}
        classNames='highlightedAppointment_button'
        unmountOnExit
        nodeRef={buttonNodeRef}
      >
        <div ref={buttonNodeRef}>
          <div className='highlightedAppointment-button'>
            <IconButton
              aria-describedby={id}
              aria-label='highlightedAppointment'
              color='primary'
              onClick={handleClick}
              size='large'
            >
              <EventAvailableIcon titleAccess={i18n._('Next appointment')} />
            </IconButton>
          </div>
          <Popover
            id={id}
            open={isOpen}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            {nextAppointment && (
              <AppointmentPreview
                appointment={nextAppointment}
                countdown={nextAppointmentCountdown}
                isOngoingAppointment={false}
                displayMode={displayMode}
                rtcServerUrl={rtcServerUrl}
                embedType='aside'
              />
            )}
          </Popover>
        </div>
      </CSSTransition>
    </>
  );
};

export default HighlightedAppointment;
